// Keeps DynamoDB and EduQuery in sync with local lesson-builder edits --
// the wrapped dispatch that tracks edited questions, auto-save on edit,
// mark-ready-and-sync-to-EduQuery, and revert-to-Draft-on-disapproval. See
// docs/BLUEPRINT-2026-10-03-lesson-builder-decomposition.md for why this is
// split from lesson-builder/page.tsx and what its boundary is.

import { useCallback, useEffect, useRef, useState } from 'react'
import { Lesson } from '@all-for-one/shared-types'
import { showToast } from 'components/toast'
import { useUpdateLessonQuestionsMutation, useUpdateLessonStatusMutation, useEditQuestionEduqueryMutation } from 'util/rtk-api'
import { buildSlidesArray, LessonAction, LessonState } from 'lesson-builder/lesson-reducer'

interface UseLessonPersistenceArgs {
  lessonId: string | null
  lessonData: Lesson | undefined
  assignedClassData: { classId: string; name: string } | undefined
  lessonState: LessonState
  baseDispatch: React.Dispatch<LessonAction>
  setAssignedClass: (c: { classId: string; name: string } | null) => void
  setIsClassAssigned: (v: boolean) => void
  setShowReadyPopup: (v: boolean) => void
}

export const useLessonPersistence = ({
  lessonId,
  lessonData,
  assignedClassData,
  lessonState,
  baseDispatch,
  setAssignedClass,
  setIsClassAssigned,
  setShowReadyPopup,
}: UseLessonPersistenceArgs) => {
  const [modifiedQuestionIds, setModifiedQuestionIds] = useState<Set<string>>(new Set())
  const [updateLessonQuestions] = useUpdateLessonQuestionsMutation()
  const [updateLessonStatus] = useUpdateLessonStatusMutation()
  const [editQuestionEduquery] = useEditQuestionEduqueryMutation()
  const prevStatusRef = useRef<string>('')

  const markQuestionAsModified = useCallback((questionId: string) => {
    setModifiedQuestionIds(prev => new Set(prev).add(questionId))
  }, [])

  // Wrap dispatch to track edited questions
  const dispatch = useCallback((action: LessonAction) => {
    if (action.type === 'EDIT_QUESTION') {
      // Find the question ID being edited to track it
      const slides = buildSlidesArray(lessonState.questionSlides, lessonState.numSlides)
      const targetSlide = slides[action.slideIndex]
      if (targetSlide && targetSlide.type === 'question') {
        const targetDiffGroup = targetSlide.questionsByDifficulty.find(d => d.difficulty === action.difficulty)
        const targetQuestion = targetDiffGroup?.questions[action.questionIndex]
        if (targetQuestion) {
          markQuestionAsModified(targetQuestion.id)
        }
      }
    }
    baseDispatch(action)
  }, [lessonState.questionSlides, lessonState.numSlides, baseDispatch, markQuestionAsModified])

  useEffect(() => {
    if (lessonData) {
      baseDispatch({
        type: 'INIT_LESSON',
        payload: lessonData
      })
    }
    if (assignedClassData) {
      setAssignedClass(assignedClassData)
      setIsClassAssigned(true)
    }
  }, [lessonData, assignedClassData])

  // Save to DynamoDB immediately when an edit happens (when "Done" is clicked)
  useEffect(() => {
    // Only save if:
    // 1. Timestamp is not null (indicating a real edit, not initial load)
    // 2. Lesson has been initialized
    if (lessonState.lastEditTimestamp !== null && lessonState.questionSlides.length > 0 && lessonId) {
      // err-10: fire-and-forget save — a failure silently lost the edits
      updateLessonQuestions({ lessonId, questions: lessonState.questionSlides }).unwrap()
        .catch(() => showToast("We couldn't save your latest edits. Please check your connection and try again."))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lessonState.lastEditTimestamp, lessonId, lessonState.questionSlides])
  // updateLessonQuestions is a stable RTK Query mutation - doesn't need dependency tracking
  // questionSlides intentionally included to catch INIT_LESSON race condition (commit fa5edd9)

  useEffect(() => {
    // Only run this effect if:
    // 1. Lesson has been initialized (questionSlides array is not empty)
    // 2. All questions are approved (totalUnapprovedQuestions === 0)
    if (lessonState.questionSlides.length > 0 && lessonState.totalUnapprovedQuestions === 0) {
      // err-10: these saves were fire-and-forget and the "Ready" popup showed
      // even when every one of them failed — edits silently lost
      const saveAndMarkReady = async () => {
        try {
          // Save questionSlides directly (no conversion needed)
          await updateLessonQuestions({ lessonId, questions: lessonState.questionSlides }).unwrap()
          // Update lesson status to Ready in DB
          await updateLessonStatus({ lessonId, status: 'Ready' }).unwrap()
        } catch (e) {
          showToast("We couldn't save your lesson as Ready. Please check your connection and approve again.")
          return
        }

        // Show popup only if user approved questions this session (not on page load)
        if (lessonState.lastEditTimestamp !== null) {
          setShowReadyPopup(true)
        }

        // Update EduQuery - only send modified questions
        lessonState.questionSlides.forEach(qSlide => {
          qSlide.questionsByDifficulty.forEach(difficultyGroup => {
            difficultyGroup.questions.forEach(question => {
              if (modifiedQuestionIds.has(question.id)) {
                editQuestionEduquery({
                  lessonId,
                  questionId: question.id,
                  question
                }).unwrap().catch(() =>
                  showToast('One of your question edits failed to sync. Please re-open and re-save that question.'))
              }
            })
          })
        })

        // Clear tracking after sync
        setModifiedQuestionIds(new Set())
      }
      saveAndMarkReady()
    }
  }, [lessonState.totalUnapprovedQuestions])

  // When a question is disapproved and lesson was Ready, update status to Draft in DB + library
  useEffect(() => {
    const prevStatus = prevStatusRef.current
    prevStatusRef.current = lessonState.status

    if (prevStatus === 'Ready' && lessonState.status === 'Draft' && lessonId) {
      updateLessonStatus({ lessonId, status: 'Draft' })
    }
  }, [lessonState.status])

  return { dispatch }
}
